import { type FormEvent, useCallback, useEffect, useState } from 'react'
import { api, type Buchung, type BuchungEingabe, type DauerauftragEingabe, type Konto } from '../api'
import { betrag, betragAlsEingabe, betragEingabe, datum, heute } from '../format'
import { DauerauftragFormular } from './Dauerauftraege'
import { Dialog, Fehlermeldung, fehlerText, KontoAuswahl, kontoArten, mitTyp } from './gemeinsam'

const SEITE = 50

function leer(): BuchungEingabe {
  return { name: '', beschreibung: '', wert: '', datum: heute(), typ: 'AUSGANG', kontoId: null, gegenkontoId: null }
}

function alsEingabe(b: Buchung): BuchungEingabe {
  return {
    name: b.name,
    beschreibung: b.beschreibung,
    wert: betragAlsEingabe(b.wert),
    datum: b.datum,
    typ: b.typ,
    kontoId: b.kontoId,
    gegenkontoId: b.gegenkontoId,
  }
}

export function Buchungen() {
  const [suche, setSuche] = useState('')
  const [liste, setListe] = useState<Buchung[]>([])
  const [gesamt, setGesamt] = useState(0)
  const [konten, setKonten] = useState<Konto[]>([])
  const [auswahl, setAuswahl] = useState<Buchung | null>(null)
  const [eingabe, setEingabe] = useState<BuchungEingabe>(leer)
  const [fehler, setFehler] = useState<string | null>(null)
  const [dauerauftrag, setDauerauftrag] = useState<DauerauftragEingabe | null>(null)

  const laden = useCallback(
    async (seiten = 1) => {
      const ergebnis = await api.buchungen(suche, 0, SEITE * seiten)
      setListe(ergebnis.eintraege)
      setGesamt(ergebnis.gesamt)
    },
    [suche],
  )

  useEffect(() => {
    const t = setTimeout(() => laden().catch((e) => setFehler(fehlerText(e))), 250)
    return () => clearTimeout(t)
  }, [laden])

  useEffect(() => {
    api.konten().then(setKonten).catch((e) => setFehler(fehlerText(e)))
  }, [])

  function waehlen(b: Buchung | null) {
    setAuswahl(b)
    setEingabe(b ? alsEingabe(b) : leer())
    setFehler(null)
  }

  async function speichern(weitere: boolean) {
    const wert = betragEingabe(eingabe.wert)
    if (wert === null) return setFehler('Bitte einen gültigen Betrag eingeben.')
    try {
      const daten = { ...eingabe, wert }
      const gespeichert = auswahl ? await api.buchungAendern(auswahl.id, daten) : await api.buchungAnlegen(daten)
      await laden(Math.max(1, Math.ceil(liste.length / SEITE)))
      if (weitere) {
        waehlen(null)
        setEingabe({ ...leer(), typ: gespeichert.typ, datum: gespeichert.datum, kontoId: gespeichert.kontoId })
      } else {
        waehlen(gespeichert)
      }
    } catch (e) {
      setFehler(fehlerText(e))
    }
  }

  async function loeschen() {
    if (!auswahl || !confirm(`Buchung „${auswahl.name}“ vom ${datum(auswahl.datum)} löschen?`)) return
    try {
      await api.buchungLoeschen(auswahl.id)
      waehlen(null)
      await laden()
    } catch (e) {
      setFehler(fehlerText(e))
    }
  }

  function dauerauftragDaraus() {
    if (!auswahl) return
    setDauerauftrag({
      periode: 'MONATLICH',
      name: auswahl.name,
      beschreibung: auswahl.beschreibung,
      wert: betragAlsEingabe(auswahl.wert),
      buchungstyp: auswahl.typ,
      start: auswahl.datum,
      ende: null,
      kontoId: auswahl.kontoId,
      gegenkontoId: auswahl.gegenkontoId,
      ausBuchungId: auswahl.id,
    })
  }

  const absenden = (e: FormEvent) => {
    e.preventDefault()
    speichern(false)
  }
  const setze = <K extends keyof BuchungEingabe>(k: K, v: BuchungEingabe[K]) => setEingabe((alt) => ({ ...alt, [k]: v }))
  const arten = kontoArten(eingabe.typ)

  return (
    <div className="zweispaltig">
      <section className="karte">
        <div className="werkzeuge">
          <input type="search" placeholder="Suche nach Name oder Beschreibung" value={suche} onChange={(e) => setSuche(e.target.value)} />
          <button className="primaer" onClick={() => waehlen(null)}>
            Neue Buchung
          </button>
        </div>
        <table className="auswahl">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Name</th>
              <th>Beschreibung</th>
              <th className="zahl">Betrag</th>
            </tr>
          </thead>
          <tbody>
            {liste.map((b) => (
              <tr key={b.id} className={b.id === auswahl?.id ? 'gewaehlt' : undefined} onClick={() => waehlen(b)}>
                <td>{datum(b.datum)}</td>
                <td>
                  {b.name}
                  {b.dauerauftragId && <span className="marke" title="aus Dauerauftrag">DA</span>}
                </td>
                <td>{b.beschreibung}</td>
                <td className="zahl">{betrag(b.wert)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="hinweis">
          {liste.length} von {gesamt} Buchungen{' '}
          {liste.length < gesamt && (
            <button className="link" onClick={() => laden(liste.length / SEITE + 1)}>
              mehr laden
            </button>
          )}
        </p>
      </section>

      <form className="karte formular" onSubmit={absenden}>
        <h2>{auswahl ? `Buchung ${auswahl.buchungsnummer}` : 'Neue Buchung'}</h2>
        <fieldset className="radios">
          {(['AUSGANG', 'EINGANG'] as const).map((t) => (
            <label key={t}>
              <input type="radio" checked={eingabe.typ === t} onChange={() => setEingabe((alt) => mitTyp(alt, 'typ', t, konten))} /> {t === 'EINGANG' ? 'Eingang' : 'Ausgang'}
            </label>
          ))}
        </fieldset>
        <label>
          Datum
          <input type="date" required value={eingabe.datum} onChange={(e) => setze('datum', e.target.value)} />
        </label>
        <label>
          Name
          <input maxLength={200} value={eingabe.name} onChange={(e) => setze('name', e.target.value)} />
        </label>
        <label>
          Beschreibung
          <input maxLength={200} value={eingabe.beschreibung} onChange={(e) => setze('beschreibung', e.target.value)} />
        </label>
        <label>
          Betrag
          <input inputMode="decimal" required value={eingabe.wert} onChange={(e) => setze('wert', e.target.value)} />
        </label>
        <label>
          Von Konto
          <KontoAuswahl konten={konten} art={arten.konto} wert={eingabe.kontoId} onChange={(id) => setze('kontoId', id)} />
        </label>
        <label>
          An Gegenkonto
          <KontoAuswahl konten={konten} art={arten.gegenkonto} wert={eingabe.gegenkontoId} onChange={(id) => setze('gegenkontoId', id)} />
        </label>
        <Fehlermeldung fehler={fehler} />
        <div className="knoepfe">
          <button type="submit" className="primaer">
            Speichern
          </button>
          <button type="button" onClick={() => speichern(true)}>
            Speichern &amp; weitere
          </button>
          {auswahl && (
            <>
              <button type="button" onClick={dauerauftragDaraus} disabled={auswahl.dauerauftragId != null}>
                Dauerauftrag daraus
              </button>
              <button type="button" className="gefahr" onClick={loeschen}>
                Löschen
              </button>
            </>
          )}
        </div>
      </form>

      <Dialog offen={dauerauftrag != null} titel="Dauerauftrag aus Buchung" onSchliessen={() => setDauerauftrag(null)}>
        {dauerauftrag && (
          <DauerauftragFormular
            start={dauerauftrag}
            konten={konten}
            onSpeichern={async (e) => {
              await api.dauerauftragAnlegen(e)
              setDauerauftrag(null)
              await laden()
            }}
          />
        )}
      </Dialog>
    </div>
  )
}
